import { useMemo, useState } from "react";
import { Box, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useReportData } from "./UseReportData";
import { ChartCard, KpiBand, PlanActualBars, PlanActualChart, Seg, SectionFrame, StatusDot, StatusKey, bucketLabel, bucketTitle, cumsum, fmt, fmtPct, fmtT, signed, statusOf, useChartColors, varPct } from "./Reportkit";

const LEVELS = {
  cls: ["Material class", (r) => r.cls], dumparea: ["Destination type", (r) => r.dumpArea], dumploc: ["Destination", (r) => r.dumpLocation],
  mat: ["Material", (r) => r.material], loadloc: ["Load location", (r) => r.loadLocation], bench: ["Bench", (r) => r.bench], loader: ["Loading unit", (r) => r.loader],
};
const HIER = {
  matdest: ["Material › Destination › Source", ["cls", "dumparea", "dumploc", "mat", "loadloc"]],
  source: ["Bench › Face › Material › Destination", ["bench", "loadloc", "mat", "dumploc"]],
  unit: ["Loading unit › Face › Destination", ["loader", "loadloc", "dumploc"]],
};

function buildTree(routes, keys) {
  const mk = (label, level, lv) => ({ label, level, lv, plan: 0, actual: 0, trips: 0, kids: new Map() });
  const root = mk("Total material moved", 0, "TMM");
  routes.forEach((r) => {
    let n = root;
    n.plan += r.plan; n.actual += r.actual; n.trips += r.trips;
    keys.forEach((k, i) => {
      const label = LEVELS[k][1](r);
      let c = n.kids.get(label);
      if (!c) { c = mk(label, i + 1, LEVELS[k][0]); n.kids.set(label, c); }
      c.plan += r.plan; c.actual += r.actual; c.trips += r.trips;
      n = c;
    });
  });
  return root;
}

export function DrillTree({ routes }) {
  const [hier, setHier] = useState("matdest");
  const [depth, setDepth] = useState(2);
  const [override, setOverride] = useState({});
  const tree = useMemo(() => buildTree(routes, HIER[hier][1]), [routes, hier]);
  const flat = [];
  const walk = (n, path) => {
    const open = override[path] ?? n.level < depth;
    flat.push({ n, path, open });
    if (open) [...n.kids.values()].sort((a, b) => b.actual - a.actual).forEach((c) => walk(c, `${path}/${c.label}`));
  };
  walk(tree, "root");
  const total = tree.actual || 1;
  return (
    <ChartCard title="Production summary — plan vs actual" sub={`Levels: TMM › ${HIER[hier][1].map((k) => LEVELS[k][0]).join(" › ")}`}
      action={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Select size="small" value={hier} onChange={(e) => { setHier(e.target.value); setOverride({}); }} sx={{ fontSize: 12 }}>
            {Object.entries(HIER).map(([k, v]) => <MenuItem key={k} value={k}>{v[0]}</MenuItem>)}
          </Select>
          <Button size="small" onClick={() => { setDepth(9); setOverride({}); }}>Expand all</Button>
          <Button size="small" onClick={() => { setDepth(1); setOverride({}); }}>Collapse all</Button>
        </Box>
      }>
      <Box sx={{ overflow: "auto", maxHeight: 560, border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>{["Hierarchy", "Plan", "Actual", "Variance", "Var %", "", "Share", "Trips"].map((h, i) => <TableCell key={i} align={i ? "right" : "left"} sx={{ fontWeight: 600 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {flat.map(({ n, path, open }) => {
              const st = statusOf(n.actual, n.plan);
              return (
                <TableRow key={path} hover sx={n.level === 0 ? { bgcolor: "action.hover", "& td": { fontWeight: 700 } } : undefined}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", pl: n.level * 2.5 }}>
                      {n.kids.size ? (
                        <IconButton size="small" onClick={() => setOverride((o) => ({ ...o, [path]: !open }))}>{open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</IconButton>
                      ) : <Box sx={{ width: 30 }} />}
                      <span>{n.label}</span>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{fmtT(n.plan)}</TableCell>
                  <TableCell align="right"><b>{fmtT(n.actual)}</b></TableCell>
                  <TableCell align="right">{signed(n.actual - n.plan, 0, " t")}</TableCell>
                  <TableCell align="right">{fmtPct(varPct(n.actual, n.plan), 1, true)}</TableCell>
                  <TableCell align="center"><StatusDot status={st} size={14} /></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>{fmtPct((n.actual / total) * 100, 1)}<Box sx={{ width: Math.max(2, Math.min(60, (n.actual / total) * 60)), height: 6, borderRadius: 1, bgcolor: "primary.main", opacity: 0.6 }} /></Box>
                  </TableCell>
                  <TableCell align="right">{fmt(n.trips)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </ChartCard>
  );
}

export default function ProductionSection({ req }) {
  const st = useReportData("production", req);
  const d = st.data;
  return (
    <SectionFrame eyebrow="Tonnes moved" title="Production Summary" desc="Plan vs actual tonnage with drill-down from material class to destination and source face" state={st}>
      {d && <Body d={d} />}
    </SectionFrame>
  );
}

function Body({ d }) {
  const c = useChartColors();
  const [mode, setMode] = useState("period");
  const t = d.totals;
  const sr = t.oreActual ? t.wasteActual / t.oreActual : null, srP = t.orePlan ? t.wastePlan / t.orePlan : null;
  const rows = useMemo(() => {
    const a = d.trend.map((x) => x.actual), p = d.trend.map((x) => x.plan);
    const A = mode === "cum" ? cumsum(a) : a, P = mode === "cum" ? cumsum(p) : p;
    return d.trend.map((x, i) => ({ label: bucketLabel(x.bucket, d.grain), title: bucketTitle(x.bucket, d.grain), actual: A[i], plan: P[i] }));
  }, [d, mode]);
  const byClass = [["Ore", t.orePlan, t.oreActual], ["Waste", t.wastePlan, t.wasteActual]].map(([name, plan, actual]) => ({ name, plan, actual }));
  return (
    <>
      <KpiBand items={[
        { label: "Total material moved", value: fmtT(t.actual), sub: `Plan ${fmtT(t.plan)}`, status: statusOf(t.actual, t.plan), delta: varPct(t.actual, t.plan) },
        { label: "Ore mined", value: fmtT(t.oreActual), sub: `Plan ${fmtT(t.orePlan)}`, status: statusOf(t.oreActual, t.orePlan), delta: varPct(t.oreActual, t.orePlan) },
        { label: "Waste moved", value: fmtT(t.wasteActual), sub: `Plan ${fmtT(t.wastePlan)}`, status: statusOf(t.wasteActual, t.wastePlan), delta: varPct(t.wasteActual, t.wastePlan) },
        { label: "Strip ratio", value: fmt(sr, 2), sub: `Plan ${fmt(srP, 2)}`, status: statusOf(sr, srP, "lower") },
        { label: "Trips", value: fmt(t.trips), sub: t.trips ? `${fmt(t.actual / t.trips, 1)} t / trip` : "" },
      ]} />
      <ChartCard title="Tonnage — plan vs actual" sub={`By ${d.grain} · bars coloured by status vs plan`}
        action={<Seg value={mode} onChange={setMode} options={[["period", "Per period"], ["cum", "Cumulative"]]} />}>
        <PlanActualChart data={rows} valueFmt={fmtT} />
        <StatusKey />
      </ChartCard>
      <DrillTree routes={d.routes} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" }, gap: 2 }}>
        <ChartCard title="Ore vs waste" sub="Plan vs actual"><PlanActualBars data={byClass} height={170} /></ChartCard>
        <ChartCard title="By shift" sub="Day vs night"><PlanActualBars data={d.byShift} height={170} /></ChartCard>
        <ChartCard title="By crew" sub="Crew recorded on the dispatch shift"><PlanActualBars data={d.byCrew} height={Math.max(170, d.byCrew.length * 46 + 40)} /></ChartCard>
      </Box>
      <span style={{ color: c.muted, fontSize: 12 }}>Plan = Completed plans only. Crew filter uses the crew recorded on each dispatch shift.</span>
    </>
  );
}